// Minimal range-selector for a sequence of indexed data points.
//
// Replaces Recharts' Brush, which renders as a chunky box. This one
// draws a thin track line, with the selected range overlaid in the
// brand colour and two rounded-corner handles vertically centred on
// the line. Pointer events cover both mouse and touch.
//
// `padLeft` / `padRight` align the track with the chart's plot-area
// edges above. `formatLabel`, when provided, renders the start/end
// values under the handles.

import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  length: number;
  startIndex: number;
  endIndex: number;
  onChange: (range: { startIndex: number; endIndex: number }) => void;
  height?: number;
  padLeft?: number;
  padRight?: number;
  formatLabel?: (idx: number) => string;
  trackColor?: string;
  rangeColor?: string;
  labelColor?: string;
}

const HANDLE_W = 8;
const HANDLE_H = 24;
const HIT_W = 22;
const HIT_H = 32;
const LABEL_GAP = 4;
const LABEL_HEIGHT = 16;

type DragMode = 'start' | 'end' | 'range';

const BrushSlider: React.FC<Props> = ({
  length,
  startIndex,
  endIndex,
  onChange,
  height = 32,
  padLeft,
  padRight,
  formatLabel,
  trackColor = '#e5e7eb',
  rangeColor = '#8884d8',
  labelColor = '#6b7280',
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // iOS Safari fires orientationchange/resize before the rotation
    // animation settles, so reading clientWidth in the handler returns
    // the pre-rotation value. Re-measure on the next frame, and again
    // after the animation typically completes (~300 ms). visualViewport
    // resize fires after iOS finishes laying out.
    const deferred = () => {
      update();
      requestAnimationFrame(update);
      setTimeout(update, 300);
    };
    window.addEventListener('resize', deferred);
    window.addEventListener('orientationchange', deferred);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', deferred);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', deferred);
      window.removeEventListener('orientationchange', deferred);
      vv?.removeEventListener('resize', deferred);
    };
  }, []);

  const trackLeft = padLeft ?? HANDLE_W;
  const trackRight = Math.max(trackLeft, width - (padRight ?? HANDLE_W));
  const trackWidth = trackRight - trackLeft;
  const lineY = height / 2;
  const totalHeight = formatLabel ? height + LABEL_GAP + LABEL_HEIGHT : height;
  const labelY = height + LABEL_GAP + LABEL_HEIGHT - 4;

  const idxToX = (idx: number): number => {
    if (length <= 1) return trackLeft;
    return trackLeft + (idx / (length - 1)) * trackWidth;
  };
  const xToIdx = (x: number): number => {
    if (length <= 1) return 0;
    const t = Math.max(0, Math.min(1, (x - trackLeft) / trackWidth));
    return Math.round(t * (length - 1));
  };

  // Refs so the long-lived pointer handlers see the latest state.
  const startRef = useRef(startIndex);
  const endRef = useRef(endIndex);
  useEffect(() => { startRef.current = startIndex; }, [startIndex]);
  useEffect(() => { endRef.current = endIndex; }, [endIndex]);

  // Wheel-zoom the selection around the cursor's data index. Attached
  // natively so we can preventDefault — React's synthetic onWheel is
  // passive. stopPropagation keeps the chart-body wheel handler from
  // also firing on the same event (it'd otherwise compound the zoom).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (length < 2 || trackWidth <= 0) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const t = Math.max(0, Math.min(1, ((e.clientX - rect.left) - trackLeft) / trackWidth));
      const anchorIdx = t * (length - 1);
      const curStart = startRef.current;
      const curEnd = endRef.current;
      const curSpan = curEnd - curStart;
      if (curSpan < 1) return;
      const factor = e.deltaY < 0 ? 0.85 : 1 / 0.85;
      const newSpan = Math.max(1, Math.min(length - 1, Math.round(curSpan * factor)));
      if (newSpan === curSpan) return;
      const f = Math.max(0, Math.min(1, (anchorIdx - curStart) / curSpan));
      let ns = Math.round(anchorIdx - f * newSpan);
      let ne = ns + newSpan;
      if (ns < 0) { ne -= ns; ns = 0; }
      if (ne > length - 1) { ns -= ne - (length - 1); ne = length - 1; }
      if (ns < 0) ns = 0;
      if (ne > length - 1) ne = length - 1;
      if (ns !== curStart || ne !== curEnd) onChange({ startIndex: ns, endIndex: ne });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [length, trackLeft, trackWidth, onChange]);

  const beginDrag = useCallback((mode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = wrapRef.current!.getBoundingClientRect();
    const downX = e.clientX;
    const initStart = startRef.current;
    const initEnd = endRef.current;

    const onMove = (ev: PointerEvent) => {
      if (mode === 'start') {
        const ns = Math.max(0, Math.min(endRef.current - 1, xToIdx(ev.clientX - rect.left)));
        if (ns !== startRef.current) onChange({ startIndex: ns, endIndex: endRef.current });
      } else if (mode === 'end') {
        const ne = Math.max(startRef.current + 1, Math.min(length - 1, xToIdx(ev.clientX - rect.left)));
        if (ne !== endRef.current) onChange({ startIndex: startRef.current, endIndex: ne });
      } else {
        const dx = ev.clientX - downX;
        const shift = trackWidth > 0 ? Math.round((dx / trackWidth) * (length - 1)) : 0;
        let ns = initStart + shift;
        let ne = initEnd + shift;
        if (ns < 0) { ne -= ns; ns = 0; }
        if (ne > length - 1) { ns -= ne - (length - 1); ne = length - 1; }
        if (ns !== startRef.current || ne !== endRef.current) onChange({ startIndex: ns, endIndex: ne });
      }
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, [length, onChange, trackWidth]);

  const sx = idxToX(startIndex);
  const ex = idxToX(endIndex);

  // Anchor the labels so they don't get clipped at the track ends:
  // start label leans into the range (anchored to its left edge),
  // end label leans the other way. In the middle range they pull
  // away from each other so the date strings don't run together.
  const startAnchor: 'start' | 'middle' | 'end' = sx < trackLeft + 40 ? 'start' : 'end';
  const endAnchor: 'start' | 'middle' | 'end' = ex > trackRight - 40 ? 'end' : 'start';
  const startLabelX = startAnchor === 'start' ? sx - HANDLE_W / 2 : sx - HANDLE_W;
  const endLabelX = endAnchor === 'end' ? ex + HANDLE_W / 2 : ex + HANDLE_W;

  return (
    <div
      ref={wrapRef}
      style={{ width: '100%', height: totalHeight, position: 'relative', userSelect: 'none', touchAction: 'none' }}
    >
      {width > 0 && (
        <svg width={width} height={totalHeight} style={{ display: 'block' }}>
          <line
            x1={trackLeft} x2={trackRight} y1={lineY} y2={lineY}
            stroke={trackColor} strokeWidth={2} strokeLinecap="round"
          />
          <line
            x1={sx} x2={ex} y1={lineY} y2={lineY}
            stroke={rangeColor} strokeWidth={2} strokeLinecap="round"
          />
          {/* Drag the whole range. Rendered before the handles so they
              get pointer priority where their hit areas overlap. */}
          <rect
            x={sx + HANDLE_W / 2}
            y={lineY - HIT_H / 2}
            width={Math.max(0, ex - sx - HANDLE_W)}
            height={HIT_H}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onPointerDown={beginDrag('range')}
          />
          <g onPointerDown={beginDrag('start')} style={{ cursor: 'pointer' }}>
            <rect
              x={sx - HIT_W / 2} y={lineY - HIT_H / 2}
              width={HIT_W} height={HIT_H} fill="transparent"
            />
            <rect
              x={sx - HANDLE_W / 2} y={lineY - HANDLE_H / 2}
              width={HANDLE_W} height={HANDLE_H} rx={3} ry={3}
              fill="white" stroke={rangeColor} strokeWidth={1.5}
            />
          </g>
          <g onPointerDown={beginDrag('end')} style={{ cursor: 'pointer' }}>
            <rect
              x={ex - HIT_W / 2} y={lineY - HIT_H / 2}
              width={HIT_W} height={HIT_H} fill="transparent"
            />
            <rect
              x={ex - HANDLE_W / 2} y={lineY - HANDLE_H / 2}
              width={HANDLE_W} height={HANDLE_H} rx={3} ry={3}
              fill="white" stroke={rangeColor} strokeWidth={1.5}
            />
          </g>
          {formatLabel && (
            <>
              <text
                x={startLabelX} y={labelY}
                textAnchor={startAnchor}
                fontSize={12} fill={labelColor}
              >
                {formatLabel(startIndex)}
              </text>
              <text
                x={endLabelX} y={labelY}
                textAnchor={endAnchor}
                fontSize={12} fill={labelColor}
              >
                {formatLabel(endIndex)}
              </text>
            </>
          )}
        </svg>
      )}
    </div>
  );
};

export default BrushSlider;
